import { getLocaleFirstDayOfWeek } from '@angular/common';
import { ChangeDetectorRef, Component, Inject, Input, LOCALE_ID, Optional, Self, SimpleChanges, ViewChild } from '@angular/core';
import { NgControl } from '@angular/forms';
import { NgbDate, NgbDateNativeAdapter, NgbDatepicker, NgbDateStruct, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { MaskitoOptions } from '@maskito/core';
import { maskitoDateOptionsGenerator } from '@maskito/kit';
import momentImported, { MomentFormatSpecification, MomentInput } from 'moment';
import { FormControlBase } from '../../common/form-control-base/form-control-base';

const moment = momentImported;

@Component({
  selector: 'waf-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  inputs: ['label', 'disabled', 'hideValidationMessages', 'isReadonlyValue', 'placeholder', 'tabindex'],
  standalone: false
})
export class DateInputComponent extends FormControlBase<string> {

  @Input() format: string;

  @Input() showOnlyMonths: boolean;

  @Input() allowTyping = true;

  @Input() minDate: string | Date;

  @Input() maxDate: string | Date;

  @Input() firstDayOfWeek: number;

  ngbMinDate: NgbDateStruct = new NgbDate(1900, 1, 1);

  ngbMaxDate: NgbDateStruct = new NgbDate(2099, 12, 31);

  today: NgbDateStruct = {} as NgbDateStruct;

  startDate: NgbDateStruct = {} as NgbDateStruct;

  maskitoOptions: MaskitoOptions = maskitoDateOptionsGenerator({
    mode: 'mm/dd/yyyy',
    separator: '/'
  });

  isYearView = false;
  increment = 0;

  @ViewChild(NgbDatepicker) private datepicker: NgbDatepicker;
  @ViewChild(NgbDropdown) private dropDown: NgbDropdown;

  private readonly Iso8601DateFormat = "YYYY-MM-DDT00:00:00";
  private ngbDateNativeAdapter: NgbDateNativeAdapter = new NgbDateNativeAdapter();
  private _displayValue: string;
  private _ngbDateModel: NgbDateStruct;

  constructor(
    @Self() @Optional() protected override ngModel: NgControl,
    @Inject(LOCALE_ID) private locale: string,
    private ref: ChangeDetectorRef
  ) {
    super('waf-date-input', ngModel);
  }

  // Display Value
  get displayValue(): string {
    return this._displayValue;
  }

  set displayValue(value: string) {
    this._displayValue = value;

    // Set our model and ngb model
    if (this.isDisplayValueValid(value)) {
      this._ngbDateModel = this.getNgbModelFromDisplayValue(value);
      this.value = this.getIso8601ValueFromDisplayValue(value);
      this.startDate = this._ngbDateModel;

      // Only navigate datepicker if we have a valid complete date
      if (!this.showOnlyMonths && this._ngbDateModel && this.datepicker) {
        setTimeout(() => this.datepicker?.navigateTo(this._ngbDateModel), 0);
      }
    } else {
      if (!value || value.length === 0) {
        this._ngbDateModel = null;
        this.value = null;
        this.startDate = this.getTodayNgbDate();
      } else {
        this.startDate = this.getDatepickerStartDateFromTypedValue(value);
        this._ngbDateModel = this.startDate;
        this.value = null;
      }
    }
  }

  // NgBootstrap Datepicker's internal model
  get ngbDateModel(): NgbDateStruct {
    return this._ngbDateModel;
  }

  set ngbDateModel(value: NgbDateStruct) {
    // NgBootstrap now parses 12/12/2 to null
    // Ignore null values from NgBootstrap dropdowns
    if (!value)
      return;

    this._ngbDateModel = value;
    this.startDate = value;

    // Set our model and display value
    if (this.isNgbDateStruct(value)) {
      const date = this.ngbDateNativeAdapter.toModel(value);
      this.value = this.getIso8601Value(date);
      this._displayValue = this.getDisplayValue(date);

      if (!this.showOnlyMonths && this.datepicker) {
        setTimeout(() => this.datepicker?.navigateTo(value), 0);
      }
    } else { // User put in some crappy value
      const stringValue = value as any;
      this.value = stringValue;
      this._displayValue = stringValue;
    }
  }

  override onInit() {
    this.startDate = this.getTodayNgbDate();
    this.setDefaultFormatIfNecessary();

    if (!this.firstDayOfWeek) {
      this.firstDayOfWeek = getLocaleFirstDayOfWeek(this.locale);
    }
    this.setupMaskitoOptions();
  }

  override ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes.format) {
      if (this.format) {
        this.format = this.format.toUpperCase();
      }
      else {
        this.setDefaultFormatIfNecessary();
      }

      this.setupMaskitoOptions();

      this.ngbDateModel = this._ngbDateModel;
      // if (this._ngbDateModel) {
      //   const date = this.ngbDateNativeAdapter.toModel(this._ngbDateModel);
      //   this._displayValue = this.getDisplayValue(date);
      // }
    }

    if (changes.showOnlyMonths && !this.format) {
      this.setDefaultFormatIfNecessary();

      if (this._ngbDateModel) {
        const date = this.ngbDateNativeAdapter.toModel(this._ngbDateModel);
        this._displayValue = this.getDisplayValue(date);
      }
    }

    if (changes.minDate) {
      this.setNgbMinDate(this.minDate);
    }

    if (changes.maxDate) {
      this.setNgbMaxDate(this.maxDate);
    }
  }

  onInputClicked(event: any) {
    this.today = this.getTodayNgbDate();

    if (!this.ngbMaxDate) {
      this.ngbMaxDate = new NgbDate(this.today.year + 10, 12, 31);
    }

    // navigate datepicker to today's date
    // const value = event.target.value;
    // if (!this.showOnlyMonths && this.datepicker && !this.isDisplayValueValid(value)) {
    //   this.startDate = this.today;
    //   this.datepicker?.navigateTo(this.today);
    // }
  }

  onInputChange(event: any) {
    if (!this.allowTyping) {
      return;
    }
    const value = event.target.value;
    if (this.isDisplayValueValid(value)) {
      this.ngbDateModel = this.getNgbModelFromDisplayValue(value);
      this.value = this.getIso8601ValueFromDisplayValue(value);
      this.closeDatePicker();
    } else {
      this._ngbDateModel = null;
      this.value = null;
      this.startDate = this.getDatepickerStartDateFromTypedValue(value);

      if (!this.showOnlyMonths && this.datepicker) {
        setTimeout(() => this.datepicker?.navigateTo(this.startDate), 0);
      }
    }
    this.displayValue = value;
  }

  onInputFocusedOut() {
    if (this.allowTyping === true) {
      if (this._displayValue && this._displayValue.trim() !== '') {
        const normalizedValue = this.normalizeYearInDisplayValue(this._displayValue);
        if (normalizedValue !== this._displayValue) {
          this._displayValue = normalizedValue;
        }
      }

      if (this._displayValue && this.isDisplayValueValid(this._displayValue)) {
        this._ngbDateModel = this.getNgbModelFromDisplayValue(this._displayValue);
        this.value = this.getIso8601ValueFromDisplayValue(this._displayValue);

        this.ngbDateModel = this._ngbDateModel;
      } else if (!this._displayValue || this._displayValue.trim() === '') {
        this._ngbDateModel = null;
        this.value = null;
        this.startDate = this.getTodayNgbDate();
      }
      this.ref.detectChanges();
      this.touch();
    }
  }

  onSelectDate(date: NgbDateStruct) {
    if (date) {
      this._ngbDateModel = date;
      this.startDate = date;
      const nativeDate = this.ngbDateNativeAdapter.toModel(date);
      this.value = this.getIso8601Value(nativeDate);
      this._displayValue = this.getDisplayValue(nativeDate);
      this.touch();
    }
    this.dropDown.close();
  }

  onSelectMonth() {
    this.dropDown.close();
  }

  closeDatePicker() {
    this.dropDown.close();
  }

  protected override onValueWritten(previousValue: string, newValue: string) {
    if (newValue === previousValue)
      return;

    if (!newValue) {
      this._ngbDateModel = null;
      this._displayValue = null;
      this.startDate = this.getTodayNgbDate();
    }
    else {
      this.ngbDateModel = this.getNgbModelFromIso8601Value(newValue);
    }
  }

  private setNgbMinDate(minDate: string | Date) {
    if (!minDate) {
      this.ngbMinDate = new NgbDate(1900, 1, 1);
      return;
    }

    if (typeof minDate === 'string') {
      const momentMinDate = this.momentWithLocale(minDate, this.Iso8601DateFormat);
      this.ngbMinDate = new NgbDate(momentMinDate.year(), momentMinDate.month() + 1, momentMinDate.date());
    } else {
      this.ngbMinDate = new NgbDate(minDate.getFullYear(), minDate.getMonth() + 1, minDate.getDate());
    }
  }

  private setNgbMaxDate(maxDate: string | Date) {
    if (!maxDate) {
      this.ngbMaxDate = new NgbDate(2099, 12, 31);
      return;
    }

    if (typeof maxDate === 'string') {
      const momentMaxDate = this.momentWithLocale(maxDate, this.Iso8601DateFormat);
      this.ngbMaxDate = new NgbDate(momentMaxDate.year(), momentMaxDate.month() + 1, momentMaxDate.date());
    } else {
      this.ngbMaxDate = new NgbDate(maxDate.getFullYear(), maxDate.getMonth() + 1, maxDate.getDate());
    }
  }

  private getNgbModelFromDisplayValue(value: string): NgbDateStruct {
    let ngbDate: NgbDateStruct = null;
    if (value) {
      this.setDefaultFormatIfNecessary();
      if (this.isInputCompleteForFormat(value)) {
        const momentDate = this.momentWithLocale(value, this.format, undefined, true); // Use strict parsing
        if (momentDate.isValid) {
          ngbDate = {
            year: momentDate.year(),
            month: momentDate.month() + 1,
            day: momentDate.date()
          } as NgbDateStruct;
        }
      }
    }
    return ngbDate;
  }

  private isInputCompleteForFormat(value: string): boolean {
    if (!value || !this.format) {
      return false;
    }
    const format = this.format.toUpperCase();
    if (format === 'MMMM YYYY') {
      return value.length >= 8 && /\s+\d{4}$/.test(value);
    }
    if (this.showOnlyMonths) {
      if (format.includes('MMMM')) {
        return value.length >= 8 && /\s+\d{4}$/.test(value);
      }
      return value.length >= 7 && value.includes('/');
    }
    const expectedLength = format.length;
    if (value.length !== expectedLength) {
      return false;
    }

    const separatorCount = (value.match(/[\/\-\.]/g) || []).length;
    const expectedSeparators = this.showOnlyMonths ? 1 : 2;

    return separatorCount === expectedSeparators;
  }

  private getNgbModelFromIso8601Value(value: string): NgbDateStruct {
    if (!value) {
      return null;
    }

    const momentDate = this.momentWithLocale(value, this.Iso8601DateFormat);
    const ngbDate = {
      day: momentDate.date(),
      month: momentDate.month() + 1,
      year: momentDate.year()
    } as NgbDateStruct;

    return ngbDate;
  }

  private getDisplayValue(value: Date): string {
    if (!value) {
      return null;
    }

    this.setDefaultFormatIfNecessary();
    return this.momentWithLocale(value).format(this.format);
  }

  private getIso8601Value(value: Date): string {
    if (!value) {
      return null;
    }

    return this.momentWithLocale(value).format(this.Iso8601DateFormat);
  }

  private getIso8601ValueFromDisplayValue(value: string): string {
    if (!value) {
      return null;
    }

    this.setDefaultFormatIfNecessary();

    return this.momentWithLocale(value, this.format).format(this.Iso8601DateFormat);
  }

  private isDisplayValueValid(value: string): boolean {
    this.setDefaultFormatIfNecessary();

    if (!this.isInputCompleteForFormat(value)) {
      return false;
    }

    return this.momentWithLocale(value, this.format, undefined, true).isValid();
  }

  private isNgbDateStruct(value: NgbDateStruct) {
    return value && value.year != null && value.month != null && value.day != null;
  }

  private setDefaultFormatIfNecessary() {
    if (this.format && this.format.trim() !== '') {
      return;
    }

    if (this.showOnlyMonths) {
      this.format = "MM/YYYY";
    }
    else {
      this.format = this.momentWithLocale().localeData().longDateFormat('L');
    }

    this.setupMaskitoOptions();
  }

  private setupMaskitoOptions(separator?: string) {
    if (!this.allowTyping) {
      this.maskitoOptions = { mask: [] };
      return;
    }

    const formatUpper = this.format?.toUpperCase();
    if (formatUpper === 'MMMM YYYY') {
      this.maskitoOptions = { mask: [] }; // No masking for text-based month input
      return;
    }
    if (!separator) {
      separator = '/';
      if (this.format) {
        if (formatUpper.includes('.')) {
          separator = '.';
        } else if (formatUpper.includes('-')) {
          separator = '-';
        }
      }
    }

    if (this.showOnlyMonths) {
      const mode = (formatUpper === 'YYYY/MM' || formatUpper === 'YYYY-MM' || formatUpper === 'YYYY.MM')
        ? 'yyyy/mm'
        : 'mm/yyyy';

      this.maskitoOptions = maskitoDateOptionsGenerator({
        mode: mode as any,
        separator: separator
      });
    } else {
      let mode = formatUpper.toLowerCase() || 'mm/dd/yyyy';

      if (this.format) {
        if (formatUpper === 'YYYY/MM/DD' || formatUpper === 'YYYY-MM-DD' || formatUpper === 'YYYY.MM.DD') {
          mode = 'yyyy/mm/dd';
        }
      }

      this.maskitoOptions = maskitoDateOptionsGenerator({
        mode: mode as any,
        separator: separator,
        min: this.minDate ? new Date(this.minDate) : new Date(1900, 0, 1),
        max: this.maxDate ? new Date(this.maxDate) : new Date(2099, 11, 31)
      });
    }
  }

  private normalizeYearInDisplayValue(value: string): string {
    if (!value || !this.format) {
      return value;
    }

    const formatUpper = this.format.toUpperCase();

    if (formatUpper === 'MMMM YYYY') {
      return value;
    }

    let separator = '/';
    if (formatUpper.includes('.')) {
      separator = '.';
    } else if (formatUpper.includes('-')) {
      separator = '-';
    }

    const parts = value.split(separator);

    let yearIndex = -1;
    const formatParts = formatUpper.split(separator);

    for (let i = 0; i < formatParts.length; i++) {
      if (formatParts[i].includes('Y')) {
        yearIndex = i;
        break;
      }
    }

    if (yearIndex === -1) {
      return value;
    }

    if (yearIndex >= parts.length) {
      const today = this.getTodayNgbDate();
      const completedParts = [...parts];
      while (completedParts.length <= yearIndex) {
        completedParts.push('');
      }
      completedParts[yearIndex] = today.year.toString();
      const completedValue = completedParts.join(separator);
      const momentCompletedDate = this.momentWithLocale(completedValue, this.format, undefined, true);
      if (momentCompletedDate.isValid()) {
        return completedValue;
      }
      return value;
    }

    const yearPart = parts[yearIndex].trim();

    if (/^\d{1,4}$/.test(yearPart)) {
      let normalizeDate;
      if (yearPart.length <= 2) {
        parts[yearIndex] = '20' + yearPart.padStart(2, '0');
        normalizeDate = parts.join(separator);
      } else {
        normalizeDate = value;
      }

      const momentNormalizeDate = this.momentWithLocale(normalizeDate, this.format, undefined, true);
      const momentMinDate = this.minDate == undefined ? this.momentWithLocale(new Date(1900, 0, 1), this.Iso8601DateFormat) : this.momentWithLocale(this.minDate, this.Iso8601DateFormat);

      if (momentNormalizeDate.isValid() && momentNormalizeDate >= momentMinDate) {
        return normalizeDate;
      } else {
        const todayNgbDate = this.getTodayNgbDate();
        const todayDate = new Date(todayNgbDate.year, todayNgbDate.month - 1, todayNgbDate.day);
        return this.momentWithLocale(todayDate).format(this.format);
      }
    }

    return value;
  }

  private getTodayNgbDate(): NgbDateStruct {
    const nativeToday = new Date();
    return {
      day: nativeToday.getDate(),
      month: nativeToday.getMonth() + 1,
      year: nativeToday.getFullYear()
    } as NgbDateStruct;
  }

  private getDatepickerStartDateFromTypedValue(value: string): NgbDateStruct {
    const today = this.getTodayNgbDate();

    if (this.showOnlyMonths || !value || value.trim() === '' || !this.format) {
      return today;
    }

    const formatUpper = this.format.toUpperCase();

    if (formatUpper === 'MMMM YYYY') {
      return today;
    }

    const separatorMatch = formatUpper.match(/[\/\-.]/);
    const separator = separatorMatch ? separatorMatch[0] : '/';

    const formatTokens = formatUpper.split(separator);
    const valueTokens = value.trim().split(separator);

    const yearIndex = formatTokens.findIndex(token => token.includes('Y'));
    const monthIndex = formatTokens.findIndex(token => token.includes('M'));
    const dayIndex = formatTokens.findIndex(token => token.includes('D'));

    let setYear;
    let setMonth;
    let setDay;

    if (yearIndex < 0 || valueTokens.length <= yearIndex) {
      setYear = today.year;
    } else {
      const yearToken = valueTokens[yearIndex]?.trim();
      const year = Number(yearToken);

      if (!yearToken || Number.isNaN(year)) {
        setYear = today.year;
      } else {
        if (/^\d{1,4}$/.test(yearToken)) {
          if (yearToken.length <= 2) {
            const nomalizedYear = '20' + yearToken.padStart(2, '0');
            setYear = Number(nomalizedYear);
          } else if (yearToken.length === 4 && year >= 1900 && year <= 2099) {
            setYear = year;
          } else {
            setYear = today.year;
          }
        }
      }
    }

    if (monthIndex < 0 || valueTokens.length <= monthIndex) {
      setMonth = today.month;
    } else {
      const monthToken = valueTokens[monthIndex]?.trim();
      const month = Number(monthToken);

      if (!monthToken || Number.isNaN(month) || month < 1 || month > 12) {
        setMonth = today.month;
      } else {
        setMonth = month;
      }
    }

    if (dayIndex < 0 || valueTokens.length <= dayIndex || !valueTokens[dayIndex]?.trim()) {
      setDay = setMonth ? 1 : today.day;
    } else {
      const dayToken = valueTokens[dayIndex]?.trim();
      const day = Number(dayToken);

      if (!dayToken || Number.isNaN(day)) {
        setDay = 1;
      } else {
        setDay = day;
      }
    }

    const setDate = this.momentWithLocale(`${setYear}-${setMonth}-${setDay}`, 'YYYY-M-D', undefined, true);
    if (!setDate.isValid()) {
      return today;
    }

    return {
      year: setYear,
      month: setMonth,
      day: setDay
    } as NgbDateStruct;
  }

  private momentWithLocale(...args: [inp?: MomentInput, format?: MomentFormatSpecification, language?: string, strict?: boolean]): momentImported.Moment {
    const mnt = moment.apply(moment, args);

    mnt.locale(this.locale);

    return mnt;
  }
}
